import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  GithubImportRequest,
  GithubImportResponse,
  GithubIntegrationStatus,
  GithubIntegrationUpsertRequest,
  GithubOrgReposResponse,
  GithubSyncResponse,
} from "@oracus/shared/api/github";

import { apiClient } from "./client.ts";

const INTEGRATION_KEY = ["github", "integration"] as const;
const ORG_REPOS_KEY = ["github", "org-repos"] as const;

export function useGithubIntegration() {
  return useQuery({
    queryKey: INTEGRATION_KEY,
    queryFn: () =>
      apiClient.get("/api/github/integration", GithubIntegrationStatus),
  });
}

export function useUpsertGithubIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GithubIntegrationUpsertRequest) =>
      apiClient.put("/api/github/integration", body, GithubIntegrationStatus),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTEGRATION_KEY });
    },
  });
}

export function useSyncGithub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.post("/api/github/sync", undefined, GithubSyncResponse),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTEGRATION_KEY });
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["repos"] });
    },
  });
}

export function useGithubOrgRepos(enabled: boolean) {
  return useQuery({
    queryKey: ORG_REPOS_KEY,
    queryFn: () =>
      apiClient.get("/api/github/org-repos", GithubOrgReposResponse),
    enabled,
    staleTime: 60_000,
  });
}

export function useImportGithubRepos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GithubImportRequest) =>
      apiClient.post("/api/github/import", body, GithubImportResponse),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORG_REPOS_KEY });
      qc.invalidateQueries({ queryKey: ["repos"] });
    },
  });
}
